import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { DMButton, DMDiv, DMText } from './Primitives';

type LeadsOverview = {
  total_leads?: number;
  source_distribution?: Record<
    string,
    | number
    | {
        count?: number;
        COUNT?: number;
        percentage?: number;
        PERCENTAGE?: number;
      }
  >;
};

interface LeadsCardProps {
  data?: LeadsOverview;
}

const fallbackSources = [
  { key: 'facebook', name: 'Facebook', color: 'bg-blue-500' },
  { key: 'instagram', name: 'Instagram', color: 'bg-fuchsia-500' },
  { key: 'youtube', name: 'Youtube', color: 'bg-red-600' },
  { key: 'website_form', name: 'Website', color: 'bg-blue-600' },
  { key: 'referrals', name: 'Parent Referrals', color: 'bg-green-500' },
  { key: 'others', name: 'Others', color: 'bg-gray-500' },
];

export const LeadsCard = ({ data }: LeadsCardProps) => {
  const navigate = useNavigate();
  const distribution = data?.source_distribution || {};

  const sourceColorByKey: Record<string, string> = {
    website_form: 'bg-blue-600',
    youtube: 'bg-red-600',
    instagram: 'bg-fuchsia-500',
    facebook: 'bg-blue-500',
    referrals: 'bg-green-500',
    others: 'bg-gray-500',
  };

  const labelByKey: Record<string, string> = {
    website_form: 'Website',
    youtube: 'Youtube',
    instagram: 'Instagram',
    facebook: 'Facebook',
    referrals: 'Parent Referrals',
    others: 'Others',
  };

  const totalLeads = Number(data?.total_leads) || 0;
  const hasDistributionData = Object.keys(distribution).length > 0;
  const normalizedSources = hasDistributionData
    ? Object.entries(distribution).map(([key, rawValue]) => {
        const isObjectValue = typeof rawValue === 'object' && rawValue !== null;
        const count =
          Number(
            isObjectValue ? rawValue.count ?? rawValue.COUNT ?? 0 : rawValue
          ) || 0;
        const explicitPercentage = isObjectValue
          ? Number(rawValue.percentage ?? rawValue.PERCENTAGE)
          : Number.NaN;

        return {
          key,
          name:
            labelByKey[key] ||
            key
              .split('_')
              .filter(Boolean)
              .map(part => part.charAt(0).toUpperCase() + part.slice(1))
              .join(' '),
          color: sourceColorByKey[key] || 'bg-gray-500',
          count,
          explicitPercentage,
        };
      })
    : fallbackSources.map(source => ({
        ...source,
        count: 0,
        explicitPercentage: Number.NaN,
      }));
  const numericDistributionTotal = normalizedSources.reduce(
    (total, source) => total + source.count,
    0
  );
  const numericValuesArePercentages =
    !normalizedSources.some(source =>
      Number.isFinite(source.explicitPercentage)
    ) &&
    numericDistributionTotal > 0 &&
    numericDistributionTotal <= 100.5 &&
    totalLeads !== numericDistributionTotal;
  const sources = normalizedSources.map(source => {
    const percentage = Number.isFinite(source.explicitPercentage)
      ? source.explicitPercentage
      : numericValuesArePercentages
      ? source.count
      : totalLeads > 0
      ? (source.count / totalLeads) * 100
      : 0;

    return {
      ...source,
      percentage: Math.max(0, Math.min(100, Math.round(percentage))),
    };
  });

  return (
    <DMDiv className="bg-white rounded-[2vh] border border-gray-200 pt-[1.8vh] px-[1.8vh] pb-[0.8vh] shadow-sm h-full flex flex-col">
      {/* Header */}
      <DMDiv className="flex items-start justify-between mb-[1.2vh] shrink-0">
        <DMDiv>
          <DMText
            as="h3"
            className="text-[1.8vh] font-bold text-gray-800 leading-tight shrink-0"
          >
            Leads by Sources
          </DMText>
          <DMText className="text-[1.25vh] text-gray-500 italic mt-[0.4vh] leading-snug">
            Every lead data is automatically sent to the admission counsellor
            mobile app
          </DMText>
        </DMDiv>
        <DMButton
          type="button"
          aria-label="Open leads management"
          onClick={() => navigate('/digital-marketing/leads')}
          className="w-[4.8vh] h-[4.8vh] bg-primary-main rounded-full flex items-center justify-center text-white shrink-0 hover:bg-primary-80 transition-colors ml-[1vh]"
        >
          <ArrowUpRight className="w-[2.5vh] h-[2.5vh]" />
        </DMButton>
      </DMDiv>

      {/* Source list – single column */}
      <DMDiv className="flex flex-col gap-[1.2vh] flex-1 overflow-auto justify-start mt-[0.2vh]">
        {sources.map(source => (
          <DMDiv
            key={`${source.key}-${source.count}-${source.percentage}`}
            className="flex items-center justify-between gap-[2vh] py-[0.6vh]"
          >
            <DMDiv className="flex min-w-0 flex-1 items-center">
              <DMText
                as="span"
                className="text-[1.7vh] font-medium text-gray-900 leading-tight truncate"
              >
                {source.name}
              </DMText>
            </DMDiv>

            <DMText
              as="span"
              className="w-[3.2vw] shrink-0 text-right text-[1.45vh] font-semibold text-gray-600"
            >
              {source.percentage}%
            </DMText>

            <DMDiv className="w-[40%] bg-gray-100 rounded-full h-[1vh] shrink-0 overflow-hidden">
              <DMDiv
                className={`${source.color} h-full rounded-full transition-all duration-500`}
                style={{ width: `${source.percentage}%` }}
              ></DMDiv>
            </DMDiv>
          </DMDiv>
        ))}
      </DMDiv>
    </DMDiv>
  );
};

